import {getTestProps} from '@benchling/js-test-utils';
import {Box, Group, StackProps, type Sx, Text, useMantineTheme} from '@mantine/core';
import React, {PropsWithChildren, ReactNode} from 'react';

import {FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import {multiLineEllipsisText} from 'styles/Utilities';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {Header} from 'ui-library/reactflow/nodes/BenchlingNode/v2/BenchlingNodeHeader';
import {
  BenchlingNodeV2Status,
  STATUS_COLORS,
} from 'ui-library/reactflow/nodes/BenchlingNode/v2/BenchlingNodeV2Constants';
import {useBenchlingNodeV2Context} from 'ui-library/reactflow/nodes/BenchlingNode/v2/useBenchlingNodeV2Context';
import DoubleClickEditableTextField, {
  DoubleClickEditableTextFieldProps,
  EmptyStringCompletionPolicy,
} from 'ui-library/reactflow/nodes/DoubleClickEditableTextField';

const NODE_HEADER_ICON_NAMES: Record<BenchlingNodeV2Status, BenchlingIconName> = {
  error: 'circle-xmark',
  'in-progress': 'timer-outline',
  pending: 'circle-outline',
  success: 'check-circle',
  warning: 'exclamation-triangle',
  // TODO(BNCH-142321): Replace 'sparkle' with 'sparkle-outline' once available in Nucleus UI
  // See: https://benchling.slack.com/archives/C03CJR3D68M/p1768951743121009
  'ai-proposed': 'sparkle',
  halted: 'do-not-enter',
};

const DISABLED_ICON_NAME: BenchlingIconName = 'lock';

export {EmptyStringCompletionPolicy};

export type DoubleClickEditableTitleProps = Omit<
  DoubleClickEditableTextFieldProps,
  'value' | 'viewer' | 'emptyStringCompletionPolicy' | 'defaultValue'
> & {
  emptyStringCompletionPolicy?: EmptyStringCompletionPolicy;
  defaultValue?: () => string;
};

export interface StandardHeaderProps {
  /** A title for the header. This is displayed in large, black text at the top of the header. */
  title: string;
  /** A subtitle for the header. This is displayed in small, gray text at the bottom of the header. */
  subTitle?: string;
  /** An icon to be displayed in the top left corner of the header. This icon is always colored gray. */
  leftIcon?: BenchlingIconName;
  /**
   * An icon to be displayed in the top right corner of the header. This icon always takes the node's status
   * color.
   * @default An icon corresponding with the node status
   */
  rightIcon?: BenchlingIconName;
  /** A ReactFlow handle to display on the left-hand side of this header. */
  leftHandle?: ReactNode;
  /** A ReactFlow handle to display on the right-hand side of this header. */
  rightHandle?: ReactNode;
  /**
   * Tooltip text to display when hovering over the disabled/lock icon.
   * Only shown when the node is disabled.
   */
  disabledTooltip?: string;
  /** When provided, makes the title double-click editable. */
  doubleClickEditableTitleProps?: DoubleClickEditableTitleProps;
}

function Title(props: {title: string; editableProps?: DoubleClickEditableTitleProps}): JSX.Element {
  const {title, editableProps} = props;
  const theme = useMantineTheme();
  if (editableProps != null) {
    return (
      <DoubleClickEditableTextField
        {...editableProps}
        value={title}
        viewer={
          <Box
            component="div"
            sx={{
              display: 'block',
              width: '100%',
              '&:hover': {
                backgroundColor: theme.colors.gray[2],
                cursor: 'text',
              },
              ...(multiLineEllipsisText(2) as Sx),
            }}
          >
            {title}
          </Box>
        }
        emptyStringCompletionPolicy={
          editableProps.emptyStringCompletionPolicy ??
          EmptyStringCompletionPolicy.REPLACE_WITH_DEFAULT_ON_COMPLETE
        }
        defaultValue={editableProps.defaultValue ?? (() => title)}
      />
    );
  }
  return <>{title}</>;
}

function RightIcon(props: {icon?: BenchlingIconName; disabledTooltip?: string}): JSX.Element | null {
  const {icon, disabledTooltip} = props;
  const {status, statusDescription, isDisabled} = useBenchlingNodeV2Context();
  const theme = useMantineTheme();

  if (isDisabled) {
    const iconEl = (
      <BenchlingIcon icon={DISABLED_ICON_NAME} style={{color: theme.colors.gray[6], height: 16, width: 16}} />
    );
    return disabledTooltip ? <Tooltip text={disabledTooltip}>{iconEl}</Tooltip> : iconEl;
  }

  const calculatedIcon = icon ?? (status != null ? NODE_HEADER_ICON_NAMES[status] : undefined);
  if (!calculatedIcon) {
    return null;
  }

  const statusColor =
    theme.colors[STATUS_COLORS[status ?? 'default'].color][STATUS_COLORS[status ?? 'default'].colorIndex];
  const iconEl = <BenchlingIcon icon={calculatedIcon} style={{color: statusColor, height: 16, width: 16}} />;
  return statusDescription ? <Tooltip text={statusDescription}>{iconEl}</Tooltip> : iconEl;
}

/**
 * A standard header for a {@link BenchlingNodeV2}. It consumes the context provided by
 * {@link BenchlingNodeV2}, and as such automatically handles node status.
 * @package
 */
export function StandardHeader(
  props: PropsWithChildren<StandardHeaderProps & Omit<StackProps, 'children'>>
): JSX.Element {
  const theme = useMantineTheme();

  const {
    leftIcon,
    rightIcon,
    leftHandle,
    rightHandle,
    title,
    subTitle,
    disabledTooltip,
    doubleClickEditableTitleProps,
    ...stackProps
  } = props;

  return (
    <Header {...stackProps} {...getTestProps({component: 'StandardHeader', element: 'root'})}>
      {leftHandle}
      <Group spacing="sm" align="flex-start">
        {leftIcon && (
          <BenchlingIcon icon={leftIcon} style={{color: theme.colors.gray[6], height: 16, width: 16}} />
        )}
        <Text
          fw={FONT_WEIGHT_PROXIMA_SEMIBOLD}
          fz="md"
          sx={{
            flex: 1,
            ...(multiLineEllipsisText(2) as Sx),
          }}
          component={doubleClickEditableTitleProps != null ? 'div' : undefined}
        >
          <Title title={title} editableProps={doubleClickEditableTitleProps} />
        </Text>
        <RightIcon icon={rightIcon} disabledTooltip={disabledTooltip} />
      </Group>
      {subTitle && (
        <Text
          c="gray.6"
          fz="sm"
          sx={{
            ...(multiLineEllipsisText(2) as Sx),
          }}
        >
          {subTitle}
        </Text>
      )}
      {rightHandle}
    </Header>
  );
}
